"use client";

import React from "react";
import { ArrowRight, ScanLine, Layers, Pill, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface HubItem {
  title: string;
  description: string;
  href: string;
  iconName: string;
}
const hubsData: HubItem[] = [{
  title: "Raw Material Identification",
  description: "Rapid, non-destructive Raman and NIR verification of raw materials directly through containers.",
  href: "/RawMaterialIdentificationPage",
  iconName: "ScanLine"
}, {
  title: "Powder Testing",
  description: "Comprehensive characterisation of powder flowability, density, and physical properties.",
  href: "/PowderTestingPage",
  iconName: "Layers"
}, {
  title: "Tablet Testing",
  description: "Pharmacopoeial testing systems for tablet dissolution, disintegration, hardness, and friability.",
  href: "/TabletTestingPage",
  iconName: "Pill"
}, {
  title: "Safety and Security",
  description: "Field-ready identification of hazardous chemicals, narcotics, and explosive materials.",
  href: "/SafetyAndSecurityPage",
  iconName: "ShieldCheck"
}];
function HubIcon({
  name
}: {
  name: string;
}) {
  const iconProps = {
    className: "size-6 text-primary",
    "aria-hidden": true
  };
  switch (name) {
    case "ScanLine":
      return <ScanLine {...iconProps} />;
    case "Layers":
      return <Layers {...iconProps} />;
    case "Pill":
      return <Pill {...iconProps} />;
    case "ShieldCheck":
      return <ShieldCheck {...iconProps} />;
    default:
      return <Layers {...iconProps} />;
  }
}
export default function SolutionHubs() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="relatedsolutionhubs" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <div className="max-w-2xl mx-auto text-center mb-12 md:mb-16">
          <span className="text-xs font-semibold tracking-wider text-primary uppercase bg-primary/10 px-3 py-1 rounded-full inline-block mb-3">
            Analytical Capabilities
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Related Solution Hubs
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground font-default">
            Explore dedicated instrumentation and compliance workflows designed for regulated laboratory and manufacturing teams.
          </p>
        </div>

        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }}>
          {hubsData.map((hub, i) => <motion.div key={hub.title} data-index={i} variants={itemVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full rounded-xl border border-border bg-card shadow-xs transition-[border-color,box-shadow,transform] duration-200 hover:border-primary/40 hover:shadow-md group">
                <div>
                  <CardHeader className="pb-4">
                    <div className="size-12 rounded-lg bg-secondary flex items-center justify-center mb-4 transition-colors group-hover:bg-primary/15">
                      <HubIcon name={hub.iconName} />
                    </div>
                    <CardTitle className="text-xl font-bold text-foreground font-default line-clamp-2">
                      {hub.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground font-default">
                      {hub.description}
                    </CardDescription>
                  </CardContent>
                </div>

                <CardFooter className="pt-4 border-t border-border/50">
                  <Button asChild variant="ghost" className="w-full justify-between px-2 text-foreground font-semibold hover:text-primary hover:bg-transparent">
                    <Link to={hub.href} className="group/link flex items-center w-full justify-between">
                      <span>Explore Solution</span>
                      <ArrowRight className="size-4 text-primary transition-transform duration-200 group-hover/link:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}